import { useEffect, useMemo, useState } from 'react'
import { Button, Divider, Flex, Modal, Select } from 'antd'
import {
  useBudgetPeriod,
  useModalRequestsCopy,
} from 'src/pages/budget/budgetRequests/hooks'
import {
  closeModalRequestsCopy,
  copyBudgetRequestFx,
  reloadBudgetRequests,
} from 'src/pages/budget/budgetRequests/model'
import { UiText } from 'src/pages/budget/components'
import { showNotification } from 'src/features/notifications'
import { formatRequests } from 'src/pages/budget/utils'
import { STATUS_PERIOD } from 'src/pages/budget/const'
import { capitalize } from 'lodash'
import dayjs from 'dayjs'

export const ModalRequestsCopy = () => {
  const modalRequestsCopy = useModalRequestsCopy()
  const { budgetPeriod } = useBudgetPeriod()
  const [budgetPeriodId, setBudgetPeriodId] = useState<number>()
  const [isCopying, setIsCopying] = useState(false)
  const [pendingIds, setPendingIds] = useState<number[]>([])

  const periodOptions = useMemo(
    () =>
      Array.from(budgetPeriod.byId.values())
        .filter((period) => period.status === STATUS_PERIOD.OPEN_CREATE)
        .sort((a, b) => a.periodFormatted.localeCompare(b.periodFormatted))
        .map((period) => ({
          label: capitalize(
            dayjs(period.periodFormatted, 'YYYY-MM').format('MMMM YYYY'),
          ),
          value: period.id,
        })),
    [budgetPeriod.byId],
  )

  useEffect(() => {
    setBudgetPeriodId(periodOptions[0]?.value)
  }, [periodOptions])

  useEffect(() => {
    setPendingIds(modalRequestsCopy?.items.map(({ id }) => id) ?? [])
  }, [modalRequestsCopy])

  if (!modalRequestsCopy) return null

  const { items } = modalRequestsCopy
  const handleCancel = () => closeModalRequestsCopy()
  const handleOk = async () => {
    if (!budgetPeriodId) return

    setIsCopying(true)
    try {
      const results = await Promise.allSettled(
        items
          .filter(({ id }) => pendingIds.includes(id))
          .map(({ id }) =>
            copyBudgetRequestFx({
              url: `/budget/budget_request/${id}/copy`,
              body: { budgetPeriodId },
            }),
          ),
      )
      reloadBudgetRequests()
      const failedIds = results.flatMap((result, index) =>
        result.status === 'rejected' ? [pendingIds[index]] : [],
      )
      if (failedIds.length === 0) {
        closeModalRequestsCopy()
      } else {
        setPendingIds(failedIds)
        showNotification({
          type: 'warning',
          message: `Не удалось скопировать заявки №${failedIds.join(', ')}`,
          description: 'Повторно будут отправлены только эти заявки.',
        })
      }
    } finally {
      setIsCopying(false)
    }
  }
  const ids = pendingIds

  return (
    <Modal
      title={
        <div style={{ fontSize: 20 }}>
          Копировать {ids.length > 1 ? 'заявки' : 'заявку'}
        </div>
      }
      centered
      wrapClassName="ant_modal"
      width={500}
      maskClosable={false}
      open
      destroyOnHidden
      onCancel={handleCancel}
      footer={
        <Flex gap={8}>
          <Button size="large" style={{ flex: 1 }} onClick={handleCancel}>
            Отменить
          </Button>
          <Button
            size="large"
            style={{ flex: 1 }}
            color="geekblue"
            variant="solid"
            onClick={handleOk}
            loading={isCopying}
            disabled={!budgetPeriodId || isCopying}
          >
            Копировать
          </Button>
        </Flex>
      }
    >
      <Divider />
      <UiText size={18}>
        Вы выбрали{' '}
        <UiText size={18} strong>
          {formatRequests(items.length)}.
        </UiText>{' '}
        Они появятся у заявителей во вкладке "Мои заявки". В какой месяц вы
        хотите их копировать:
      </UiText>
      <Select
        size="large"
        style={{ width: '100%', marginTop: 16 }}
        placeholder="Выберите месяц"
        value={budgetPeriodId}
        options={periodOptions}
        onChange={setBudgetPeriodId}
      />
      <Divider />
    </Modal>
  )
}
